import * as PhotoUpload from "shared/util/PhotoUpload";
import ClientStats from "shared/util/ClientStats";
import _str from "shared/util/_str";
import Context from "shared/util/Context";

import {
	insertDraggingPhoto,
	getFeedback,
	uploadFacebookPhotoAndDetails,
} from "apps/photo_organizer/util/helpers";
import {
	MIN_PHOTO_SIZE,
	LAYOUTS,
	FEEDBACK_STATES,
	TOTAL_PHOTO_COUNTS,
	UPLOAD_STYLE,
	SORT_BY,
} from "apps/photo_organizer/util/constants";

import {
	UPLOAD_FILE,
	UPLOAD_FILE_SUCCESS,
} from "ducks/photo_upload";

import { PREVIOUS_STEP } from "ducks/onboarding";


// ////////////////////////
// ACTION TYPES //////////
// //////////////////////

export const INITIALIZE_PHOTO_ORGANIZER = "PHOTO_ORGANIZER.INITIALIZE_PHOTO_ORGANIZER";
export const CHANGE_TO_SORT_BY_LIKES = "PHOTO_ORGANIZER.CHANGE_TO_SORT_BY_LIKES";

export const INSTRUCTIONS_SHOW = "PHOTO_ORGANIZER.INSTRUCTIONS_SHOW";
export const INSTRUCTIONS_DISMISS = "PHOTO_ORGANIZER.INSTRUCTIONS_DISMISS";

export const REORDER = "PHOTO_ORGANIZER.REORDER";

export const FETCH_FB_PHOTOS = "PHOTO_ORGANIZER.FETCH_FB_PHOTOS";
export const FETCH_FB_PHOTOS_SUCCESS = "PHOTO_ORGANIZER.FETCH_FB_PHOTOS_SUCCESS";
export const FETCH_FB_PHOTOS_FAILURE = "PHOTO_ORGANIZER.FETCH_FB_PHOTOS_FAILURE";

export const DELETE_PHOTO = "PHOTO_ORGANIZER.DELETE_PHOTO";
export const DELETE_PHOTO_SUCCESS = "PHOTO_ORGANIZER.DELETE_PHOTO_SUCCESS";
export const DELETE_PHOTO_FAILURE = "PHOTO_ORGANIZER.DELETE_PHOTO_FAILURE";
export const DELETE_PHOTO_UNDO = "PHOTO_ORGANIZER.DELETE_PHOTO_UNDO";

export const RESET_DRAG_DROP = "PHOTO_ORGANIZER.RESET_DRAG_DROP";
export const SET_DRAGGING_PHOTO = "PHOTO_ORGANIZER.SET_DRAGGING_PHOTO";
export const SET_NEW_INDEX = "PHOTO_ORGANIZER.SET_NEW_INDEX";

export const UPLOAD_START = "PHOTO_ORGANIZER.UPLOAD_START";
export const UPLOAD_PHOTO = "PHOTO_ORGANIZER.UPLOAD_PHOTO";

export const UPLOAD_DETAILS_SUCCESS = "PHOTO_ORGANIZER.UPLOAD_DETAILS_SUCCESS";
export const UPLOAD_DETAILS_MISSES = "PHOTO_ORGANIZER.UPLOAD_DETAILS_MISSES";

export const UPLOAD_FACEBOOK_PHOTO_SUCCESS = "PHOTO_ORGANIZER.UPLOAD_FACEBOOK_PHOTO_SUCCESS";
export const UPLOAD_FACEBOOK_PHOTO_FAILURE = "PHOTO_ORGANIZER.UPLOAD_FACEBOOK_PHOTO_FAILURE";

export const REORDER_PHOTOS = "PHOTO_ORGANIZER.REORDER_PHOTOS";
export const REORDER_PHOTOS_SUCCESS = "PHOTO_ORGANIZER.REORDER_PHOTOS_SUCCESS";
export const REORDER_PHOTOS_FAILURE = "PHOTO_ORGANIZER.REORDER_PHOTOS_FAILURE";

export const ANIMATION_START = "PHOTO_ORGANIZER.ANIMATION_START";
export const ANIMATION_FINISH = "PHOTO_ORGANIZER.ANIMATION_FINISH";

export const CLEAR_ERROR = "PHOTO_ORGANIZER.CLEAR_ERROR";


// ////////////////////////
// INITIAL STATE /////////
// //////////////////////

export const INITIAL_STATE = {
	// from the server
	userid: null,
	facebookAlbums: [],
	profilePhotos: [],
	layout: LAYOUTS.HERO,
	uploadStyle: UPLOAD_STYLE.multiple,
	sortBy: SORT_BY.date,

	// app state
	dismissedInstructions: false,
	isAnimating: false,
	isFetchingFacebookPhotos: false,
	fetchedFacebookPhotos: false,
	feedbackMessage: getFeedback({ state: FEEDBACK_STATES.none }),

	// is uploading a single photo
	isUploadingPhoto: false,

	// is doing their final save
	isUploadingFinal: false,
	isUploadingFinalComplete: false,

	// this is a dictionary of all phtoos
	photoMap: {},

	// this is the canonical order of photo ids
	photos: [],

	// this is a last-deleted-last list of deleted photo ids
	photosDeleted: [],

	// keep track of the photoids you're dragging and replacing
	// and swap them in the COMPONENT, not in the STATE
	draggingPhotoId: null,
	newIndex: null,

	error: null,

	// to keep track of uploads and deletes
	stats: {
		initialFacebookCount: 0,
		fbPhotosDeleted: 0,
		photosDeleted: 0,
		photosAdded: 0,
	},
};

// Pull in initial state from the window, if provided.
const initialState = typeof window !== "undefined" && window.PhotoOrganizerParams ?
	{ ...INITIAL_STATE, ...window.PhotoOrganizerParams } :
	INITIAL_STATE;


// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export function reducer(state = initialState, action) {
	switch (action.type) {
		case INITIALIZE_PHOTO_ORGANIZER: {
			const data = action.payload;

			return {
				...state,
				...data,
			};
		}

		case INSTRUCTIONS_SHOW: {
			return {
				...state,
				dismissedInstructions: false,
			};
		}

		case CHANGE_TO_SORT_BY_LIKES: {
			return {
				...state,
				sortBy: SORT_BY.likes,
			};
		}

		case INSTRUCTIONS_DISMISS: {
			return {
				...state,
				dismissedInstructions: true,
			};
		}

		case FETCH_FB_PHOTOS: {
			return {
				...state,
				fetchedFacebookPhotos: false,
				isFetchingFacebookPhotos: true,
			};
		}
		case FETCH_FB_PHOTOS_SUCCESS: {
			const photos = action.photos

				// remove photos that are too small and thus uggo
				.filter(({ width, height }) => width > MIN_PHOTO_SIZE && height > MIN_PHOTO_SIZE)

				// just in case... only keep photos with actual sources
				.filter((photo) => photo.source)

				// output a dev-friendly object
				.map((photo, i) => {
					const {
						width, height, id, source, likes,
					} = photo;

					// compress likes to a number
					const likeCount = !likes || !likes.data ?
						0 :
						likes.data.length;

					return {
						isFacebookPhoto: true,

						// tag this once and forever
						// (isFacebookPhoto is removed after upload)
						wasFacebookPhoto: true,
						thumb: source,
						likes: likeCount,
						id,
						source,
						width,
						height,
						i,
					};
				});

			// sort the photos in-place
			if (state.sortBy === SORT_BY.likes) {
				photos.sort((a, b) => {
					return b.likes - a.likes;
				});
			}

			const originalPhotoCount = state.photos.length;

			const photoIds = state.photos
				.concat(photos.map((p) => p.id))
				.filter((p, i) => {
					/*
						WELCOME TO HACK COUNTRY
						when someone connects to FB, the backend
						automatically downloads the first photo
						and sets that as their profile photo.
						but of course, we're probably re-downloading
						that photo.
						so if the user has exactly 1 photo,
						ignore the first photo.
					*/

					if (state.sortBy === SORT_BY.date &&
						originalPhotoCount === 1 && i === 0) {
						return false;
					}

					return i < TOTAL_PHOTO_COUNTS[state.layout];
				});

			const initialFacebookCount = photoIds.length - state.photos.length;

			return {
				...state,
				error: null,
				isFetchingFacebookPhotos: false,
				fetchedFacebookPhotos: true,
				photos: photoIds,
				photoMap: {
					...state.photoMap,

					// add a profile photo to each item, and convert it to a dict
					...photos.reduce((memo, p) => ({ ...memo, [p.id]: p }), {}),
				},
				stats: {
					...state.stats,
					initialFacebookCount,
				},
			};
		}
		case FETCH_FB_PHOTOS_FAILURE: {
			return {
				...state,
				isFetchingFacebookPhotos: false,
				fetchedProfilePhotos: true,
				error: _str("Oops! We couldn’t fetch your Facebook photos."),
			};
		}

		case UPLOAD_START: {
			return {
				...state,
				isUploadingFinal: true,
				error: null,
				dismissedInstructions: true,
			};
		}

		case UPLOAD_FACEBOOK_PHOTO_SUCCESS: {
			return {
				...state,
				photoMap: {
					...state.photoMap,
					[action.picid]: {
						...state.photoMap[action.picid],
						okcid: action.okcid,
						hasUploadSuccess: true,
					},
				},
			};
		}

		case UPLOAD_FACEBOOK_PHOTO_FAILURE: {
			return {
				...state,
				photoMap: {
					...state.photoMap,
					[action.picid]: {
						...state.photoMap[action.picid],
						hasUploadSuccess: false,
					},
				},
			};
		}

		case REORDER_PHOTOS_SUCCESS: {
			const { photos, photoMap } = state;
			const hasPhotoFailures = photos.reduce((memo, id) => {
				if (photoMap[id].hasUploadSuccess === false) {
					return true;
				}

				return memo;
			}, false);

			const photoFailureError = _str("Some photos couldn’t be uploaded. Continuing anyway…");

			return {
				...state,
				isUploadingFinalComplete: true,
				error: hasPhotoFailures ? photoFailureError : null,
				feedbackMessage: hasPhotoFailures ? getFeedback({
					state: FEEDBACK_STATES.error,
					error: photoFailureError,
				}) : getFeedback({ state: FEEDBACK_STATES.none }),
			};
		}

		case REORDER_PHOTOS_FAILURE: {
			const error = _str("We had trouble reordering your photos. Continuing anyway…");
			return {
				...state,
				error,
				isUploadingFinalComplete: true,
				feedbackMessage: getFeedback({
					state: FEEDBACK_STATES.error,
					error,
				}),
			};
		}

		case RESET_DRAG_DROP: {
			const { photos, draggingPhotoId, newIndex } = state;

			return {
				...state,
				photos: insertDraggingPhoto({
					photos,
					draggingPhotoId,
					newIndex,
				}),
				draggingPhotoId: null,
				newIndex: null,
			};
		}

		case ANIMATION_START: {
			return {
				...state,
				isAnimating: true,
			};
		}

		case ANIMATION_FINISH: {
			return {
				...state,
				isAnimating: false,
			};
		}

		case SET_DRAGGING_PHOTO: {
			// when you start dragging, that photo's the first thing you hover.
			// so to avoid triggering a first animation, set this photo's
			// index to the shit.
			// in a stupid way.

			const newIndex = state.photos.indexOf(action.id);

			if (newIndex === -1) {
				return state;
			}

			return {
				...state,
				newIndex,
				error: null,
				draggingPhotoId: action.id,
				dismissedInstructions: true,
			};
		}
		case SET_NEW_INDEX: {
			return {
				...state,
				newIndex: action.index,
			};
		}

		case DELETE_PHOTO: {
			return {
				...state,
				error: null,
				dismissedInstructions: true,
				photos: state.photos.filter((id) => id !== action.id),
				photosDeleted: state.photosDeleted.concat([action.id]),
				feedbackMessage: getFeedback({
					state: FEEDBACK_STATES.deleting,
					photoCount: state.photos.length,
				}),
			};
		}

		case DELETE_PHOTO_SUCCESS: {
			const wasFBPhoto = state.photoMap[action.id].wasFacebookPhoto;
			let { fbPhotosDeleted, photosDeleted } = state.stats;

			if (wasFBPhoto) {
				fbPhotosDeleted += 1;
			} else {
				photosDeleted += 1;
			}

			return {
				...state,
				stats: {
					...state.stats,
					fbPhotosDeleted,
					photosDeleted,
				},
			};
		}

		case DELETE_PHOTO_FAILURE: {
			const { index } = action;

			// if the photo COULD be returned to its old index, do that
			let photos;

			if (index !== null && index < state.photos.length) {
				photos = state.photos;
				photos.splice(index, 0, action.id);
			} else {
				photos = state.photos.concat([action.id]);
			}

			const error = _str("Oops! We couldn’t delete that photo. Please try again.");

			return {
				...state,
				photos,
				photosDeleted: state.photosDeleted.filter((id) => id !== action.id),
				error,
				feedbackMessage: getFeedback({
					state: FEEDBACK_STATES.error,
					error,
				}),
			};
		}

		case DELETE_PHOTO_UNDO: {
			const { photosDeleted } = state;
			const lastDeleted = photosDeleted[photosDeleted.length - 1];
			return {
				...state,
				photosDeleted: photosDeleted.filter((id) => id !== lastDeleted),
				photos: state.photos.concat([lastDeleted]),
			};
		}

		case UPLOAD_FILE: {
			return {
				...state,
				isUploadingPhoto: true,
				error: null,
			};
		}

		case UPLOAD_FILE_SUCCESS: {
			const {
				id, picid, src, width, height, thumb,
			} = action;

			// action comes back with either id or picd
			const photoId = id || picid;

			// only proceed if the photo isn't in here already
			if (state.photoMap[photoId]) {
				return state;
			}

			if (!photoId) {
				return {
					...state,
					error: _str("Oops! We couldn’t upload that photo. Please try again."),
				};
			}

			const photo = {
				isFacebookPhoto: false,
				id: photoId,
				okcid: photoId,
				thumb,
				source: src,
				width,
				height,
			};

			const photos = [photo.id].concat(state.photos);
			const totalPhotos = TOTAL_PHOTO_COUNTS[state.layout];

			let feedback;

			if (photos.length === totalPhotos) {
				feedback = getFeedback({
					state: FEEDBACK_STATES.complete,
				});
			} else {
				feedback = getFeedback({
					state: FEEDBACK_STATES.adding,
					photoCount: photos.length,
				});
			}

			return {
				...state,
				isUploadingPhoto: false,
				photos,
				error: null,
				feedbackMessage: feedback,
				photoMap: {
					...state.photoMap,
					[photo.id]: photo,
				},
				stats: {
					...state.stats,
					photosAdded: state.stats.photosAdded + 1,
				},
			};
		}

		case CLEAR_ERROR: {
			return {
				...state,
				error: null,
			};
		}

		// hook into the onboarding reducer to reset some shit when we go back from
		// the next set to the photo organizer
		case PREVIOUS_STEP: {
			return {
				...state,
				isUploadingFinal: false,
				isUploadingFinalComplete: false,
			};
		}

		default:
			return state;
	}
}

// ////////////////////////
// ACTION CREATORS ///////
// //////////////////////

export function initializePhotoOrganizer(payload) {
	return {
		type: INITIALIZE_PHOTO_ORGANIZER,
		payload,
	};
}

export function sortByLikes() {
	return { type: CHANGE_TO_SORT_BY_LIKES };
}

export function showInstructions() {
	return { type: INSTRUCTIONS_SHOW };
}

export function dismissInstructions() {
	return { type: INSTRUCTIONS_DISMISS };
}

export function fetchFacebookPhotos() {
	return (dispatch, getStore) => {
		const store = getStore();
		const { facebookAlbums } = store.photoOrganizer;

		if (facebookAlbums.length) {
			dispatch({ type: FETCH_FB_PHOTOS });

			// for now, pluck the first album's id.
			PhotoUpload.getFacebookAlbumPhotos({ id: facebookAlbums[0] })
				.then(({ data }) => {
					if (data.length) {
						dispatch({ type: FETCH_FB_PHOTOS_SUCCESS, photos: data });
					} else {
						throw new Error("Photo upload error");
					}
				})
				.catch(() => {
					dispatch({ type: FETCH_FB_PHOTOS_FAILURE });
				});
		}
	};
}

export function setDraggingPhoto(id) {
	return {
		type: SET_DRAGGING_PHOTO,
		id,
	};
}

export function setNewIndex(index) {
	return {
		type: SET_NEW_INDEX,
		index,
	};
}

export function resetDragDrop() {
	return {
		type: RESET_DRAG_DROP,
	};
}

export function startAnimation() {
	return {
		type: ANIMATION_START,
	};
}

export function finishAnimation() {
	return {
		type: ANIMATION_FINISH,
	};
}

export function deletePhoto(id) {
	return (dispatch, getStore) => {
		const store = getStore();
		const { photoMap, photos } = store.photoOrganizer;

		// do the failure mode a big favor and save the old index
		const index = photos.reduce((memo, pid, i) => (pid === id ? i : memo), null);

		// first, dispatch a delete item
		dispatch({ type: DELETE_PHOTO, id });

		// then, if it's an okc photo, we have to delete it on the server.
		// if the photo has a key isFacebookPhoto, then we don't have to delete it
		// from our server because it ain't there!
		if (!photoMap[id] || photoMap[id].isFacebookPhoto) {
			return;
		}

		PhotoUpload.deletePhoto(id)
			.then(() => {
				dispatch({ type: DELETE_PHOTO_SUCCESS, id });
			})
			.catch(() => {
				dispatch({ type: DELETE_PHOTO_FAILURE, id, index });
			});
	};
}

export function undoDelete() {
	return { type: DELETE_PHOTO_UNDO };
}

export function uploadFacebookPhoto(picid) {
	return (dispatch, getStore) => {
		const store = getStore();

		const userid = store.photoOrganizer.userid || Context.get("userid");
		const source = store.photoOrganizer.photoMap[picid].source;

		ClientStats.update("onboarding - photo organizer - fb photo - attempted");

		uploadFacebookPhotoAndDetails(userid, source)
			.then((res) => {
				dispatch({
					type: UPLOAD_FACEBOOK_PHOTO_SUCCESS,
					okcid: res.picid,
					picid,
				});
				ClientStats.update("onboarding - photo organizer - fb photo - success");
			})
			.catch((e) => {
				dispatch({
					type: UPLOAD_FACEBOOK_PHOTO_FAILURE,
					picid,
				});
				ClientStats.update("onboarding - photo organizer - fb photo - failure");

				if (typeof window.onerror === "function") {
					window.onerror(
						`Photo uploading error: ${JSON.stringify(e)}`,
						"onboarding.min.js",
						593,
						0,
						e,
					);
				}
			});
	};
}

export function setPhotoOrder() {
	return (dispatch, getStore) => {
		const { photoOrganizer } = getStore();
		const { photos, photoMap } = photoOrganizer;

		const hasFb = photos.reduce((memo, photo) => {
			if (photoMap[photo].isFacebookPhoto) {
				return true;
			}

			return memo;
		}, false);

		ClientStats.update(`onboarding - photo organizer - facebook:${hasFb} - setting order`);
		ClientStats.update("onboarding - photo organizer - reorder - attempted");

		dispatch({ type: REORDER_PHOTOS });

		const photoDetails = photos
			.filter((id) => photoMap[id].okcid)
			.map((id, i) => ({
				picid: photoMap[id].okcid,
				ordinal: i,
			}));

		// make a map of photo ids to make checking easier
		const photoDetailMap = photoDetails.reduce((memo, { picid }) => {
			return {
				...memo,
				[picid]: true,
			};
		}, {});

		PhotoUpload.setPhotoOrder(photoDetails)
			.then((res) => {
				// delete any photos that don't match up with that fucker
				const extraPhotoIds = res.updated_photos
					.map((photo) => photo.id)
					.filter((id) => !photoDetailMap[id]);

				if (extraPhotoIds.length) {
					extraPhotoIds.map(PhotoUpload.deletePhoto);
				}

				dispatch({
					type: REORDER_PHOTOS_SUCCESS,

					// send the thumbs so other parts of the app can show
					// the new thumb
					thumbs: res.updated_profile_thumbs,
				});

				ClientStats.update("onboarding - photo organizer - reorder - success");
			})
			.catch((e) => {
				dispatch({ type: REORDER_PHOTOS_FAILURE });

				ClientStats.update("onboarding - photo organizer - reorder - failure");

				if (typeof window.onerror === "function") {
					window.onerror(
						`Photo ordering error (photo_organizer): ${JSON.stringify(e)}`,
						"onboarding.min.js",
						649,
						0,
						e,
					);
				}
			});
	};
}

export function uploadPhotos() {
	return (dispatch, getStore) => {
		const { photoOrganizer } = getStore();
		const { photos, photoMap } = photoOrganizer;

		const hasFb = photos.reduce((memo, photo) => {
			if (photoMap[photo].isFacebookPhoto) {
				return true;
			}

			return memo;
		}, false);

		ClientStats.update(`onboarding - photo organizer - facebook:${hasFb} - uploading new photos`);
		return dispatch({ type: UPLOAD_START });
	};
}

export function clearError() {
	return { type: CLEAR_ERROR };
}



// WEBPACK FOOTER //
// ./src/ducks/photo_organizer.js